import { Link } from "react-router-dom";
import { CheckCircle2, Video, Sparkles, Heart, ArrowUpRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SITE, SERVICES, SPECIALTIES } from "@/lib/site";

export default function Services() {
  return (
    <div className="min-h-screen">
      <Navigation />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-10 md:pb-14">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="glow-orb top-20 -right-20 w-[420px] h-[420px] bg-primary/25" />
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Expert psychiatric care
          </div>
          <h1 data-testid="services-title" className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-balance">
            What <em className="not-italic gradient-text font-light">we treat.</em>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto text-pretty">
            Mental health care for children, teens, and adults — telehealth across {SITE.state}.
          </p>
        </div>
      </section>

      <main className="pb-10 md:pb-14">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">

            {/* Telehealth banner */}
            <div className="mt-6 mb-14 flex items-center gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground shrink-0">
                <Video className="w-5 h-5" />
              </span>
              <p className="text-base md:text-lg leading-snug">
                Secure telehealth across {SITE.state} — from the comfort of home.
              </p>
            </div>

            {/* Service cards — alternating */}
            <div className="space-y-6">
              {SERVICES.map((s, i) => (
                <article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="group rounded-3xl border border-border bg-card/40 overflow-hidden transition-all hover:border-primary/40"
                >
                  <div className={`grid md:grid-cols-12 ${i % 2 === 0 ? "" : "md:[&>div:first-child]:order-2"}`}>
                    <div className="md:col-span-5 relative aspect-[4/3] md:aspect-auto md:min-h-[320px] overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-card/90 via-card/30 to-transparent" />
                      <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-background/70 backdrop-blur border border-border">
                        {String(i + 1).padStart(2, "0")} · {s.short}
                      </span>
                    </div>
                    <div className="md:col-span-7 p-6 md:p-10 flex flex-col justify-center">
                      <div className="flex items-start gap-4">
                        <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary shrink-0">
                          <CheckCircle2 className="w-5 h-5" />
                        </span>
                        <div>
                          <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3 group-hover:text-primary transition-colors">
                            {s.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">{s.description}</p>
                          <Link
                            to={`/contact?service=${encodeURIComponent(s.short)}`}
                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-2.5 transition-all"
                          >
                            Request appointment <ArrowUpRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Specialties */}
            <section className="mt-8 md:mt-12 rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-8">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary">
                  <Heart className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl tracking-tight">We specialize in</h2>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SPECIALTIES.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 rounded-xl border border-border bg-background/40 px-5 py-3.5"
                  >
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* CTA */}
            <section className="mt-16 rounded-3xl border border-primary/30 bg-gradient-to-br from-card to-primary/10 p-6 md:p-10 text-center relative overflow-hidden">
              <div className="glow-orb -top-20 -right-10 w-[320px] h-[320px] bg-primary/30" />
              <div className="relative z-10">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">
                  Now accepting new patients
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                  Telehealth across {SITE.state}. We&rsquo;d love to help.
                </p>
                <Button
                  asChild
                  size="lg"
                  data-testid="services-cta-portal"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-7"
                >
                <Link to="/contact">
                  New patient? Start here
                </Link>
                </Button>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
